import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bot, BarChart3, MessageSquare, Settings, User, Phone, FileSearch, DollarSign, Webhook, Activity, Code, PhoneCall, Shield, BookOpen, Building2, Clock, PlugZap, Archive, FileSpreadsheet, MessagesSquare, Route, Wand2, Globe, Radar, MousePointerClick } from 'lucide-react';
import Logo from './Logo';
import { kbWebScraper } from '../api';

const DashboardSidebar = ({ activeSection, onSectionChange, isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Saved websites that changed since their last sync (KB Web Scraper change monitor)
  const [changedSites, setChangedSites] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => kbWebScraper.listChanges()
      .then((r) => { if (alive) setChangedSites(r.data?.count || 0); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(timer); };
  }, [activeSection]);
  const badges = { 'website-monitoring': changedSites };

  const navigationItems = [
    { id: 'overview', title: 'Overview', icon: BarChart3 },
    { id: 'agents', title: 'Voice Agents', icon: Bot },
    { id: 'archived-agents', title: 'Archived Agents', icon: Archive },
    { id: 'conversations', title: 'Conversations', icon: MessageSquare },
    { id: 'call-history-export', title: 'Call History & Export', icon: FileSpreadsheet },
    { id: 'chat-history-export', title: 'Chat History & Export', icon: MessagesSquare },
    { id: 'session-analysis', title: 'Session Analysis', icon: FileSearch },
    { id: 'ivr-qa', title: 'IVR QA', icon: Route },
    { id: 'phone-numbers', title: 'Phone Numbers', icon: Phone },
    { id: 'outbound-api', title: 'Outbound API', icon: PhoneCall },
    { id: 'pii-preview', title: 'PII Preview', icon: Shield },
    { id: 'knowledge-base', title: 'Knowledge Base', icon: BookOpen },
    { id: 'kb-optimizer', title: 'KB Optimizer', icon: Wand2 },
    { id: 'kb-web-scraper', title: 'KB Web Scraper', icon: Globe },
    { id: 'website-monitoring', title: 'Website Monitoring', icon: Radar },
    { id: 'cobrowse', title: 'Co-Browsing', icon: MousePointerClick },
    { id: 'usage', title: 'Usage', icon: Clock },
    { id: 'costs', title: 'Cost & Pricing', icon: DollarSign },
    { id: 'webhooks', title: 'Webhooks', icon: Webhook },
    { id: 'webhook-events', title: 'Webhook Events', icon: Activity },
    { id: 'custom-functions', title: 'Custom Functions', icon: Code },
    { id: 'integrations', title: 'Integrations', icon: PlugZap },
    { id: 'profile', title: 'Profile', icon: User },
    { id: 'workspace', title: 'Workspace', icon: Settings },
  ];

  return (
    <div className={`h-screen border-r border-border flex flex-col transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'}`} style={{ backgroundColor: 'hsl(var(--sidebar-background))' }}>
      {/* Sidebar Header */}
      <div className="p-6 border-b border-border">
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-start'}`}>
          <Logo showText={!isCollapsed} size={isCollapsed ? "lg" : "lg"} />
        </div>
      </div>

      {/* Sidebar Content */}
      <div className="flex-1 overflow-y-auto py-4">
        {/* Navigation Menu */}
        <div className={`${isCollapsed ? 'px-2' : 'px-4'} space-y-2`}>
          {navigationItems.map((item) => {
            const handleClick = () => {
              onSectionChange(item.id);
            };
            
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={handleClick}
                className={`
                  w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-start'} ${isCollapsed ? 'px-2 py-4' : 'px-4 py-3'} rounded-xl transition-all duration-300
                  ${isActive
                    ? 'bg-primary text-primary-foreground shadow-glow'
                    : 'text-foreground hover:bg-muted/50'
                  }
                `}
              >
                <span className="relative flex-shrink-0">
                  <item.icon className={`${isCollapsed ? 'w-8 h-8' : 'w-5 h-5 mr-3'}`} />
                  {isCollapsed && badges[item.id] > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-warning" />}
                </span>
                {!isCollapsed && <span className="font-medium">{item.title}</span>}
                {!isCollapsed && badges[item.id] > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-warning text-[11px] font-semibold text-black flex items-center justify-center"
                    title={`${badges[item.id]} saved website${badges[item.id] === 1 ? '' : 's'} changed since the last sync`}
                  >
                    {badges[item.id]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={onToggleCollapse}
        className={`${isCollapsed ? 'p-4' : 'p-4'} border-t border-border hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground`}
      >
        <Settings className={`${isCollapsed ? 'w-8 h-8 mx-auto' : 'w-5 h-5 rotate-180'}`} />
      </button>
    </div>
  );
};

export default DashboardSidebar;

